import React, { useEffect, useRef, useState } from "react";
import { Users, Package, Star, Award } from "lucide-react";
import useSiteSettings from "utils/useSiteSettings";
import LakeWaves from "components/LakeWaves";
import { Eyebrow } from "components/SectionHeading";

/* Тоог 0-ээс зорилтот утга хүртэл харагдах үед тоолох */
const CountUp = ({ value }) => {
  const ref = useRef(null);
  const [disp, setDisp] = useState(String(value ?? ""));

  useEffect(() => {
    const s = String(value ?? "");
    const m = s.match(/^(\D*)([\d.,]+)(.*)$/);
    if (!m) { setDisp(s); return; }
    const pre = m[1];
    const numStr = m[2].replace(/,/g, "");
    const suf = m[3];
    const target = parseFloat(numStr);
    const decimals = (numStr.split(".")[1] || "").length;
    if (isNaN(target)) { setDisp(s); return; }

    const reduce = typeof window !== "undefined" && window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const el = ref.current;
    if (reduce || !el || typeof IntersectionObserver === "undefined") { setDisp(s); return; }

    const fmt = (n) =>
      pre + new Intl.NumberFormat("mn-MN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(n) + suf;

    setDisp(fmt(0));
    let started = false;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started) {
          started = true;
          io.disconnect();
          const dur = 1300;
          const t0 = performance.now();
          const tick = (t) => {
            // rAF-ийн эхний кадрын цаг t0-оос өмнө байж болно — сөрөг тоо харуулахгүй
            const p = Math.min(1, Math.max(0, (t - t0) / dur));
            const eased = 1 - Math.pow(1 - p, 3);
            setDisp(fmt(target * eased));
            if (p < 1) requestAnimationFrame(tick);
            else setDisp(s);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [value]);

  return <span ref={ref}>{disp}</span>;
};

const Stats = () => {
  const { get } = useSiteSettings();

  const stats = [
    { icon: Users, value: get("stats.value1"), label: get("stats.label1") },
    { icon: Package, value: get("stats.value2"), label: get("stats.label2") },
    { icon: Award, value: get("stats.value3"), label: get("stats.label3") },
    { icon: Star, value: get("stats.value4"), label: get("stats.label4") },
  ];

  return (
    <section className="pb-20 bg-slate-50">
      <div className="container mx-auto px-4">
        {/* Бараан хавтан — Хөвсгөл нуурын гүн: гадаргуугийн цагираг + зөөлөн давалгаа */}
        <div className="relative overflow-hidden rounded-3xl bg-brand-blue-dark text-white px-6 py-12 md:px-12 md:py-14">
          <div className="absolute inset-0 bg-ripple pointer-events-none" />
          <LakeWaves
            layers={["fill-white/[0.03]", "fill-white/[0.05]"]}
            className="absolute bottom-0 inset-x-0 h-20 md:h-28"
          />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-4">
              <Eyebrow>Тоо баримт</Eyebrow>
              <h2 className="mt-3 font-display text-3xl md:text-4xl font-extrabold">{get("stats.title")}</h2>
              <p className="mt-3 text-lg text-white/65">{get("stats.subtitle")}</p>
            </div>

            <div className="lg:col-span-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
              {stats.map((stat, index) => (
                <div key={index} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                  <stat.icon className="w-5 h-5 text-primary" />
                  <div className="mt-4 font-display text-xl sm:text-2xl md:text-3xl font-extrabold tabular-nums break-words">
                    <CountUp value={stat.value} />
                  </div>
                  <div className="mt-1 text-sm text-white/65">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Stats;
